import React from 'react';
import ReactDOM from 'react-dom';
import { useState, useRef, useEffect } from 'react';

const ProductSearchDropDown = ({ options = [], value, onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [displayedOptions, setDisplayedOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  useEffect(() => {
    if (typeof value !== 'undefined') setInputValue(value);
  }, [value]);

  useEffect(() => {
    setDisplayedOptions(options || []);
  }, [options]);

  useEffect(() => {
    const updateMenuStyle = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'absolute',
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
      });
    };

    if (menuIsOpen) {
      updateMenuStyle();
      window.addEventListener('resize', updateMenuStyle);
      window.addEventListener('scroll', updateMenuStyle, true);
    }

    return () => {
      window.removeEventListener('resize', updateMenuStyle);
      window.removeEventListener('scroll', updateMenuStyle, true);
    };
  }, [menuIsOpen]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setMenuIsOpen(true);
    const filtered = (options || []).filter(option => option.label.toLowerCase().includes(newValue.toLowerCase()));
    setDisplayedOptions(filtered);
    onChange(newValue);
  };

  const handleOptionSelect = (option) => {
    setInputValue(option.label);
    onChange(option);
    setMenuIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < displayedOptions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleOptionSelect(displayedOptions[highlightedIndex]);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setMenuIsOpen(false), 150);
  };

  return (
    <div>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setMenuIsOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        ref={inputRef}
        className="outline-blue-500 text-sm w-full h-full p-2 bg-transparent rounded-md"
      />
      {menuIsOpen && menuStyle && ReactDOM.createPortal(
        <div ref={optionsRef} style={menuStyle} className="bg-white border border-gray-300 rounded-md shadow-lg z-[9999] max-h-48 overflow-y-auto">
          {displayedOptions.map((option, idx) => (
            <div
              key={option.value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleOptionSelect(option)}
              className={`px-3 py-2 cursor-pointer ${idx === highlightedIndex ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}`}>
              {option.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductSearchDropDown;
